/**
 * Chat screen container component
 * @remarks
 * Manages all business logic for chat screen including:
 * - Chat state management
 * - Message handling
 * - Keyboard behavior
 * - Research mode
 * - Render callbacks
 * Delegates UI rendering to ChatView.
 */

/* eslint-disable max-lines -- Container manages complex chat logic including state, callbacks, and mobile UI element creation */

import { useReactiveVar } from "@apollo/client";
import { useHeaderHeight } from "expo-router/react-navigation";
import { Href, useLocalSearchParams, useRouter } from "expo-router";
import { Search } from "lucide-react-native";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Keyboard,
  Linking,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  useWindowDimensions,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import {
  Bubble,
  BubbleProps,
  IMessage,
  InputToolbarProps,
  Send,
  SendProps,
  User,
} from "react-native-gifted-chat";
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller";
import { useAnimatedStyle } from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import ChatView from "./ChatView";
import ChatInput from "@/features/knowledge-graph/components/molecules/ChatInput";
import ChatInputToolbar from "@/features/knowledge-graph/components/organisms/ChatInputToolbar";
import { LoadingDots } from "@/features/knowledge-graph/components/molecules/LoadingDots";
import MarkdownRenderer from "@/features/knowledge-graph/components/molecules/MarkdownRenderer";
import MessageBubble from "@/features/knowledge-graph/components/organisms/MessageBubble";
import QueryProgressBar from "@/features/knowledge-graph/components/molecules/QueryProgressBar";
import SafeAndroidMarkdown from "@/features/knowledge-graph/components/molecules/SafeAndroidMarkdown";
import SuggestedQuestionItem from "@/features/knowledge-graph/components/molecules/SuggestedQuestionItem";
import { useChat } from "../../hooks/useChat";
import { useChatConfigOperations } from "../../hooks/useChatConfigOperations";
import { useChatTheme } from "../../hooks/useChatTheme";
import { useMessageOperations } from "../../hooks/useMessageOperations";
import { usePrefillAutoSend } from "../../hooks/usePrefillAutoSend";
import { useReconnectOperations } from "../../hooks/useReconnectOperations";
import { useStatusOperations } from "../../hooks/useStatusOperations";
import { useThreadOperations } from "../../hooks/useThreadOperations";
import { bubbleMaxWidth } from "../../utils/bubbleMaxWidth";

import { Box } from "@/components/atoms/Box";
import { FlashList, type FlashListRef } from "@/components/atoms/FlashList";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { TAB_BAR_BASE_HEIGHT } from "@/config/constants";
import { ASK_GEMINI_RESEARCH_TOGGLE } from "@/config/featureFlags";
import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";
import {
  connectionStatusVar,
  currentStatusVar,
  reconnectAttemptsVar,
} from "@/stores/knowledgeGraph";
import { TEST_ID_GEMINI_CHAT } from "e2e/testId";
import { RADIUS } from "@/config/radius";

const aiUser: User = {
  _id: "ai",
  name: "Assistant",
  avatar: "https://ui-avatars.com/api/?name=AI&background=0D8ABC&color=fff",
};

/*
 * design-system: dynamic-style waiver — data-driven theme colours, measured
 * chat layout widths, margin-bearing wrappers, and RN text-flow/transform
 * props that no closed token expresses; passed as single style references so
 * no inline object literal renders on a JSX element.
 */
const colorStyle = (color: string): TextStyle => ({ color });
const bubbleMaxWidthStyle = (windowWidth: number): ViewStyle => ({
  maxWidth: bubbleMaxWidth(windowWidth),
});

/**
 * The desktop suggested-questions block's column (SE-8864).
 * @param props - Component props
 * @param props.width - Current window width, for the column's cap
 * @param props.expanded - Whether the cards are showing
 * @param props.children - The heading row and the cards
 * @returns The block, centred in the composer's column
 * @remarks The block sat at the left of the chat, about half its width, while
 * the composer below it was centred and capped. This is the composer's own
 * column (ChatInputToolbarView): `items-center` around a `w-full` box capped
 * at the same bubble ladder, so both share their left and right edges.
 */
const SuggestedColumn = ({
  width,
  expanded,
  children,
}: {
  readonly width: number;
  readonly expanded: boolean;
  readonly children: ReactNode;
}) => (
  <Box className={`items-center px-4 ${expanded ? "py-4" : "pb-2"}`}>
    <Box
      className="w-full"
      testID={TEST_ID_GEMINI_CHAT.SUGGESTED_COLUMN}
      // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: measured responsive maxWidth from windowWidth (continuous multiplier); no closed sizing token
      UNSAFE_style={
        bubbleMaxWidthStyle(width) as Record<string, string | number>
      }
    >
      {children}
    </Box>
  </Box>
);
/*
 * QA-reported oversized query bubble: lineHeight must be an explicit
 * "22px" STRING on web. React DOM's inline-style engine treats a bare
 * NUMBER for CSS
 * `line-height` as a unitless multiplier of font-size (spec-compliant —
 * this is how CSS `line-height: 1.5` works), not pixels, while React
 * Native interprets a numeric `lineHeight` as absolute dp. `UNSAFE_style`
 * hands this object straight to the DOM `style` prop, bypassing
 * react-native-web's own StyleSheet compiler (which normally appends
 * "px"), so the same `22` that renders a normal 22dp line on iOS/Android
 * computed to `22 × 18px font-size = 396px` on web — ballooning the
 * single-line user query bubble into an oversized box (empty space above
 * and below the text) at every viewport width. Verified live on staging:
 * the rendered span's height dropped from 396px to 22px the instant the
 * unit was added.
 */
const userMessageTextStyle = (
  color: string
): { color: string; lineHeight: number | string } => ({
  color,
  lineHeight: Platform.OS === "web" ? "22px" : 22,
});
const sendTextStyle = (color: string): TextStyle => ({
  color,
});
const chevronStyle = (color: string, expanded: boolean): TextStyle => ({
  color,
  // CSS-string transform (not an RN transform array) so it applies on the web
  // Box/Text atom's div; RN 0.85 accepts the string form on native too.
  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
});
/**
 * The three width bands the suggested-questions layout switches on.
 */
type SuggestedLayoutBand = "mobile" | "tablet" | "desktop";

/**
 * Classifies the window into its suggested-questions layout band.
 * @param windowWidth - Current window width
 * @param isMobile - The narrow/wide split the view renders with
 * (`useMobileScreen()`)
 * @returns The band the window falls into
 * @remarks
 * SE-8193 found this band and `ChatView`'s renderer split a pixel apart at
 * 1280. SE-8674 takes the desktop band from the same `useMobileScreen()`
 * value the view forks on, so the two cannot disagree at any width.
 */
export const suggestedLayoutBand = (
  windowWidth: number,
  isMobile: boolean
): SuggestedLayoutBand => {
  if (!isMobile) return "desktop";
  return windowWidth < 768 ? "mobile" : "tablet";
};

/** Suggested-questions layout style per width band — a flat lookup, no nested branching. */
const SUGGESTED_LAYOUT_BY_BAND: Record<
  SuggestedLayoutBand,
  Pick<ViewStyle, "flexDirection" | "gap" | "flexWrap">
> = {
  mobile: { flexDirection: "column", gap: 8, flexWrap: "nowrap" },
  tablet: { flexDirection: "column", gap: 12, flexWrap: "nowrap" },
  desktop: { flexDirection: "row", gap: 12, flexWrap: "wrap" },
};

const suggestedLayoutStyle = (
  windowWidth: number,
  isMobile: boolean
): ViewStyle =>
  SUGGESTED_LAYOUT_BY_BAND[suggestedLayoutBand(windowWidth, isMobile)];
const errorBoxStyle = (isDark: boolean): ViewStyle => ({
  backgroundColor: isDark ? "#4a1a1a" : "#ffebee",
  padding: 10,
  // marginLeft/Right instead of RN-only marginHorizontal so the horizontal
  // margin actually applies on the web Box atom's div (same 10px each side).
  marginLeft: 10,
  marginRight: 10,
  marginTop: 10,
  borderRadius: 6,
  borderLeftWidth: 4,
  borderLeftColor: "#f44336",
});
const errorTextStyle = (isDark: boolean): TextStyle => ({
  color: isDark ? "#ff6666" : "#c62828",
});

/**
 * A question the user sent from the narrow layout that the list has not yet
 * been brought to.
 */
interface PendingSend {
  /** Message count when it was sent; the question lands at or after it. */
  readonly fromIndex: number;
  /** The question as sent, which is the text of the user message it adds. */
  readonly text: string;
}

/**
 * Finds the user message a send added, once it is in the list.
 * @param messages - The conversation
 * @param pending - The send being waited on
 * @returns Its index, or -1 while it has not arrived
 */
const findSentQuestionIndex = (
  messages: readonly IMessage[],
  pending: PendingSend
): number =>
  messages.findIndex(
    (message, index) =>
      index >= pending.fromIndex &&
      message.user?._id !== aiUser._id &&
      message.text === pending.text
  );

/** A sent question the narrow list is bringing into view. */
interface QuestionPin {
  /** The question's index in the conversation. */
  readonly index: number;
  /** Its id, so a replaced conversation releases the pin. */
  readonly messageId: IMessage["_id"];
  /** Its row has been in view, so its layout is measured, not a placeholder. */
  readonly seen: boolean;
  /** The answer has finished streaming. */
  readonly ending: boolean;
  /** Passes run since the stream ended; a cap so a pin never outlives it. */
  readonly endPasses: number;
  /** Passes in a row that saw no content or window change. */
  readonly quietPasses: number;
  /** The geometry version the last pass saw. */
  readonly seenVersion: number;
}

/** The list's real geometry, from the scroll view's own events. */
interface ListGeometry {
  /** Scroll offset. */
  offset: number;
  /** Content height, including the padding that clears the composer. */
  content: number | undefined;
  /** Window height. */
  window: number | undefined;
  /** Bumped on every content or window size change. */
  version: number;
}

/** The refs one screen's question pin works through. */
interface QuestionPinRefs {
  /** The pin being held. */
  readonly pin: RefObject<QuestionPin | null>;
  /** The narrow layout's message list. */
  readonly list: RefObject<FlashListRef<IMessage> | null>;
  /** The pass waiting for its frame, if any. */
  readonly frame: RefObject<number | null>;
  /** Removes the web listeners that hand control back to the user. */
  readonly detach: RefObject<(() => void) | null>;
  /**
   * The list's real geometry. FlashList's own `getAbsoluteLastScrollOffset`
   * runs ahead after a big jump — it read 5933 against a real 3573 on the
   * web export — so the scroll view's events are the source instead.
   */
  readonly geometry: RefObject<ListGeometry>;
}

/** How close to its target the list may sit and count as there. */
const PIN_TOLERANCE = 2;

/**
 * Frames the list must stay put, with no content or window change, after the
 * stream ends before the pin lets go. The last tokens and the stream's end
 * can land in one render, before the scroll view has reported the content
 * they add; letting go on the first settled pass read that stale height.
 */
const PIN_QUIET_PASSES = 10;

/** End-of-stream passes (about 3 s) after which the pin lets go regardless. */
const PIN_MAX_END_PASSES = 180;

/**
 * Reads where the pinned question's row starts, in scroll-offset terms.
 * @param list - The narrow layout's message list
 * @param pin - The pin
 * @returns The row's top, or undefined while its row has never been in view
 * @remarks
 * FlashList's `getLayout` stores a zero-size placeholder for a row it has not
 * laid out yet, and reading one that early stranded the list at its bottom
 * on the local web export — so it is only read once the row has been seen.
 */
const pinnedQuestionTop = (
  list: FlashListRef<IMessage>,
  pin: QuestionPin
): number | undefined => {
  if (!pin.seen) return undefined;
  const layout = list.getLayout(pin.index);
  return layout ? layout.y + list.getFirstItemOffset() : undefined;
};

/**
 * The list's true end: content height, which includes the bottom padding
 * that clears the absolutely positioned composer, less the window height.
 * FlashList's own end leaves that padding out, so `scrollToIndex` clamped a
 * short answer underneath the composer.
 * @param geometry - The list's real geometry
 * @returns The largest scroll offset, or undefined until both are known
 */
const trueEndOffset = (geometry: ListGeometry): number | undefined =>
  geometry.content === undefined || geometry.window === undefined
    ? undefined
    : Math.max(0, geometry.content - geometry.window);

/**
 * Lets go of the pin, drops any pass still waiting for its frame and removes
 * the listeners it added.
 * @param refs - The screen's pin refs
 */
const releaseQuestionPin = (refs: QuestionPinRefs): void => {
  const pinRef = refs.pin;
  const frameRef = refs.frame;
  const detachRef = refs.detach;
  pinRef.current = null;
  detachRef.current?.();
  detachRef.current = null;
  if (frameRef.current === null) return;
  cancelAnimationFrame(frameRef.current);
  frameRef.current = null;
};

/**
 * Runs one pass of the question pin: scroll to the lower of the question's
 * top and the list's true end.
 * @param refs - The screen's pin refs
 * @remarks
 * While the content below the question is shorter than the window, that is
 * the true end — the question, its status rows and the answer all sit above
 * the composer. Once the end lies beyond the question's top, the question is
 * held at the top and the answer grows below it unchased. Passes repeat on
 * every content or window change, so they converge as FlashList measures
 * rows it had only estimated. When the stream has ended, the pin lets go on
 * the first pass that has nothing left to move.
 */
const runQuestionPin = (refs: QuestionPinRefs): void => {
  const pinRef = refs.pin;
  const pin = pinRef.current;
  const list = refs.list.current;
  if (!pin || !list) return;
  const { startIndex, endIndex } = list.computeVisibleIndices();
  const seen = pin.seen || (startIndex <= pin.index && pin.index <= endIndex);
  const questionTop = pinnedQuestionTop(list, { ...pin, seen });
  const geometry = refs.geometry.current;
  const trueEnd = trueEndOffset(geometry);
  const holdQuestion =
    questionTop !== undefined && trueEnd !== undefined && questionTop < trueEnd;
  const target = holdQuestion ? questionTop : trueEnd;
  const settled =
    target !== undefined && Math.abs(geometry.offset - target) <= PIN_TOLERANCE;
  const quietPasses =
    geometry.version === pin.seenVersion ? pin.quietPasses + 1 : 0;
  if (
    pin.ending &&
    ((settled && quietPasses >= PIN_QUIET_PASSES) ||
      pin.endPasses >= PIN_MAX_END_PASSES)
  ) {
    releaseQuestionPin(refs);
    return;
  }
  if (!settled && holdQuestion)
    list.scrollToOffset({ offset: questionTop, animated: false });
  if (!settled && !holdQuestion)
    list.getNativeScrollRef()?.scrollToEnd({ animated: false });
  pinRef.current = {
    ...pin,
    seen,
    endPasses: pin.ending ? pin.endPasses + 1 : 0,
    quietPasses,
    seenVersion: geometry.version,
  };
  if (pin.ending) scheduleQuestionPin(refs);
};

/**
 * Queues one pin pass for the next frame, so new rows are laid out before
 * the scroll; a burst of updates shares the pending frame.
 * @param refs - The screen's pin refs
 */
const scheduleQuestionPin = (refs: QuestionPinRefs): void => {
  const frameRef = refs.frame;
  if (frameRef.current !== null) return;
  frameRef.current = requestAnimationFrame(() => {
    frameRef.current = null;
    runQuestionPin(refs);
  });
};

/**
 * On the web, a mouse wheel or touch drag over the list hands control back
 * to the user. (Native drags arrive through the list's `onScrollBeginDrag`.)
 * @param refs - The screen's pin refs
 */
const attachUserScrollRelease = (refs: QuestionPinRefs): void => {
  const node = refs.list.current?.getScrollableNode() as
    | Pick<HTMLElement, "addEventListener" | "removeEventListener">
    | null
    | undefined;
  if (Platform.OS !== "web" || !node) return;
  const detachRef = refs.detach;
  const onUserScroll = () => releaseQuestionPin(refs);
  node.addEventListener("wheel", onUserScroll, { passive: true });
  node.addEventListener("touchmove", onUserScroll, { passive: true });
  detachRef.current = () => {
    node.removeEventListener("wheel", onUserScroll);
    node.removeEventListener("touchmove", onUserScroll);
  };
};
const flashListStyle = (isDark: boolean): ViewStyle => ({
  backgroundColor: isDark ? "#000" : "#fff",
});

/** The absolute composer's height: its ~80pt input row plus its 8pt pad. */
const COMPOSER_CLEARANCE = 88;

/**
 * The narrow list's content padding.
 * SE-5440: the tab header is opaque and in-flow on both platforms, so the old
 * hardcoded 120px iOS top pad was dead space that let the pinned response card
 * scroll its title up under the header. The bottom pad clears the
 * absolutely-positioned input on iOS/web; Android's input is in normal flow.
 * SE-8807: the iOS composer pads by 8, not the inset the tab bar already
 * covers, and rises by `keyboardHeight - tabBarHeight` with the keyboard open,
 * so the list clears exactly that — before, it ignored the keyboard and the
 * raised composer covered the progress steps. Web keeps the inset. The tab bar
 * height is the container's `tabBarHeight`, the same shared constant.
 * @param bottomInset - Bottom safe area inset
 * @param iosKeyboardHeight - iOS keyboard height (0 when closed or not iOS)
 */
const mobileListContentStyle = (
  bottomInset: number,
  iosKeyboardHeight: number
): ViewStyle => {
  const iosPad =
    COMPOSER_CLEARANCE + Math.max(0, iosKeyboardHeight - TAB_BAR_BASE_HEIGHT);
  const nonAndroidPad =
    Platform.OS === "web" ? bottomInset + COMPOSER_CLEARANCE : iosPad;
  return {
    paddingHorizontal: 0,
    paddingTop: 8,
    paddingBottom: Platform.OS === "android" ? 8 : nonAndroidPad,
  };
};

/**
 * Whether a sent query is still waiting for its answer to start (SE-8646).
 * @param messages - The conversation, oldest first
 * @param isLoading - Whether a query is in flight
 * @returns True from the moment a query is sent until the first token lands
 * (which starts an assistant message) or the stream ends (which clears
 * `isLoading`)
 * @remarks
 * Before this the mobile footer waited for the first status event, so the
 * screen sat blank for ~10 s after sending.
 */
const isAwaitingFirstToken = (
  messages: readonly IMessage[],
  isLoading: boolean
): boolean => isLoading && messages.at(-1)?.user?._id !== "ai";

/**
 * Suggested question data structure
 */
interface SuggestedQuestion {
  icon: string;
  text: string;
  query: string;
}

/** The chat route's search params: a prefilled question and autofocus. */
type ChatParams = {
  q?: string;
  autofocus?: string;
};

/**
 * Chat screen container - manages business logic and state
 * @remarks Uses Apollo reactive variables for state management where available
 */
const ChatContainer = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { colors } = useChatTheme();
  const [showStatusSettings, setShowStatusSettings] = useState(false);
  const [showSuggestedQuestions, setShowSuggestedQuestions] = useState(true);
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = useMobileScreen();
  const router = useRouter();
  const { q: prefillQuery, autofocus } = useLocalSearchParams<ChatParams>();
  const shouldAutofocusInput = autofocus === "1";
  const getExpandedUrl = useGetExpandedPlayerUrl();
  const headerHeight = useHeaderHeight();
  const { bottom: bottomInset } = useSafeAreaInsets();

  // Keyboard animation for mobile input positioning (iOS)
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation();

  // Android: track keyboard height — edge-to-edge ignores adjustResize, so
  // the overlaying keyboard must be cleared manually (SE-5440)
  const [androidKeyboardHeight, setAndroidKeyboardHeight] = useState(0);

  // Tab bar clearance subtracted from the keyboard height (SE-5440). Only
  // the BASE height is subtracted: the open keyboard overlays the bottom
  // safe-area inset region itself, and Samsung keyboards under-report their
  // height by roughly the suggestion-toolbar row — the inset differential
  // absorbs that. Mirrors tabBarStyle height in (tabs)/_layout.tsx;
  // expo-router SDK 56 forbids @react-navigation/bottom-tabs
  // (useBottomTabBarHeight), hence the shared constant.
  const tabBarHeight = TAB_BAR_BASE_HEIGHT;

  // iOS native: track keyboard height for static positioning (avoids Reanimated 4.x issues)
  const [iosKeyboardHeight, setIosKeyboardHeight] = useState(0);

  const inputAnimatedStyle = useAnimatedStyle(() => ({
    bottom: Platform.OS === "ios" ? -keyboardHeight.value : 0,
  }));

  // Check if research toggle feature is enabled
  const isResearchToggleEnabled = ASK_GEMINI_RESEARCH_TOGGLE === "on";

  // State for research mode
  const [isResearchMode, setIsResearchMode] = useState(false);

  // SE-8674: the narrow layout's message list, and the user's send it has
  // yet to bring into view (see the effect that consumes it).
  const mobileListRef = useRef<FlashListRef<IMessage>>(null);
  const pendingSendRef = useRef<PendingSend | null>(null);
  const questionPinRef = useRef<QuestionPin | null>(null);
  const pinFrameRef = useRef<number | null>(null);
  const pinDetachRef = useRef<(() => void) | null>(null);
  const mobileListGeometryRef = useRef<ListGeometry>({
    offset: 0,
    content: undefined,
    window: undefined,
    version: 0,
  });
  const pinRefs = useMemo<QuestionPinRefs>(
    () => ({
      pin: questionPinRef,
      list: mobileListRef,
      frame: pinFrameRef,
      detach: pinDetachRef,
      geometry: mobileListGeometryRef,
    }),
    []
  );
  /** Keeps the list's real scroll offset for the pin. */
  const onMobileListScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      mobileListGeometryRef.current = {
        ...mobileListGeometryRef.current,
        offset: event.nativeEvent.contentOffset.y,
      };
    },
    []
  );
  /** The list's content resized — a streamed line, a status row: re-check. */
  const onMobileListContentSize = useCallback(
    (_width: number, height: number) => {
      mobileListGeometryRef.current = {
        ...mobileListGeometryRef.current,
        content: height,
        version: mobileListGeometryRef.current.version + 1,
      };
      if (questionPinRef.current) scheduleQuestionPin(pinRefs);
    },
    [pinRefs]
  );
  /** The list's window resized — a keyboard hid or showed: re-check. */
  const onMobileListLayout = useCallback(
    (event: LayoutChangeEvent) => {
      mobileListGeometryRef.current = {
        ...mobileListGeometryRef.current,
        window: event.nativeEvent.layout.height,
        version: mobileListGeometryRef.current.version + 1,
      };
      if (questionPinRef.current) scheduleQuestionPin(pinRefs);
    },
    [pinRefs]
  );
  /** The user took hold of the list: the pin lets go. */
  const onMobileListUserScroll = useCallback(
    () => releaseQuestionPin(pinRefs),
    [pinRefs]
  );

  // Use the chat hook for all chat functionality
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    canSendMessage,
    canSendMessageWithText,
  } = useChat();

  // Get state from reactive variables
  const connectionStatus = useReactiveVar(connectionStatusVar);
  const currentStatus = useReactiveVar(currentStatusVar);
  const reconnectAttempts = useReactiveVar(reconnectAttemptsVar);

  // Get operations from hooks
  const { clearMessages } = useMessageOperations();
  const { resetReconnectAttempts } = useReconnectOperations();
  const { resetChatConfigs, hideStatusDisplay } = useChatConfigOperations();
  const { clearAgentProgress } = useStatusOperations();
  const { createNewThread } = useThreadOperations();

  // Handle link presses for Android markdown (currently unused but ready for future implementation)
  const _handleAndroidLinkPress = useCallback(
    (url: string) => {
      try {
        // Handle player links
        if (url.includes("/players/") || url.includes("player/")) {
          const urlParts = url.split("/");
          const playerId = urlParts[urlParts.length - 1];
          if (playerId) {
            // For now, just navigate to the player route
            // In a real implementation, you'd need to resolve the UUID
            router.push(getExpandedUrl(`/players/${playerId}`) as Href);
          }
        } else if (url.startsWith("http")) {
          // Handle external links
          if (Platform.OS === "web") {
            window.open(url, "_blank");
          } else {
            Linking.openURL(url).catch(err => {
              const errorMessage =
                err instanceof Error ? err.message : "Unable to open URL";
              Alert.alert("Error", `Failed to open URL: ${errorMessage}`);
            });
          }
        }
      } catch (error) {
        console.error("Error handling link press:", error);
      }
    },
    [router, getExpandedUrl]
  );

  const onSend = useCallback(
    (newMessages: readonly IMessage[] = []) => {
      try {
        if (newMessages.length === 0 || !canSendMessage || isLoading) return;
        const userMessage = newMessages[0];
        if (!userMessage) return;
        const text = userMessage.text || " ";
        if (!text || !canSendMessageWithText(text)) return;
        void sendMessage(text, isResearchMode);
        Keyboard.dismiss();
        setShowSuggestedQuestions(false);
      } catch (error) {
        console.error("Error in onSend:", error);
      }
    },
    [
      sendMessage,
      canSendMessage,
      canSendMessageWithText,
      isLoading,
      isResearchMode,
    ]
  );

  /**
   * Callback for ChatInput to send text messages
   * @param text - Message text
   * @param researchMode - Whether research mode is enabled
   */
  const onSendText = useCallback(
    (text: string, researchMode: boolean) => {
      try {
        if (!canSendMessage || isLoading || !canSendMessageWithText(text))
          return;
        pendingSendRef.current = { fromIndex: messages.length, text };
        void sendMessage(text, researchMode);
        Keyboard.dismiss();
        setShowSuggestedQuestions(false);
      } catch (error) {
        console.error("Error in onSendText:", error);
      }
    },
    [
      sendMessage,
      canSendMessage,
      canSendMessageWithText,
      isLoading,
      messages.length,
    ]
  );

  /**
   * Toggle suggested questions visibility
   */
  const onToggleSuggestedQuestions = useCallback(() => {
    setShowSuggestedQuestions(prev => !prev);
  }, []);

  /**
   * Toggle research mode
   */
  const onToggleResearch = useCallback(() => {
    setIsResearchMode(prev => !prev);
  }, []);

  /**
   * Toggle status settings visibility
   */
  const onToggleStatusSettings = useCallback(() => {
    setShowStatusSettings(prev => !prev);
  }, []);

  /**
   * Handle suggested question press
   */
  const onSuggestedQuestionPress = useCallback(
    (query: string) => {
      setShowSuggestedQuestions(false);
      void sendMessage(query, isResearchMode);
    },
    [sendMessage, isResearchMode]
  );

  /**
   * Render message text content with markdown support for AI messages
   */
  const renderMessageText = useCallback(
    (messageProps: { currentMessage: IMessage | undefined }) => {
      if (messageProps.currentMessage?.user?._id === aiUser._id) {
        return (
          <Box
            className="overflow-hidden rounded-md p-2"
            // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: measured responsive maxWidth from windowWidth (continuous multiplier); no closed sizing token
            UNSAFE_style={
              bubbleMaxWidthStyle(windowWidth) as Record<
                string,
                string | number
              >
            }
          >
            {Platform.OS === "android" ? (
              <SafeAndroidMarkdown
                text={messageProps.currentMessage.text}
                isDark={isDark}
              />
            ) : (
              <MarkdownRenderer content={messageProps.currentMessage.text} />
            )}
          </Box>
        );
      }
      // Check if research mode was active for this message
      const hasResearchMode = !!(
        messageProps.currentMessage as IMessage & {
          metadata?: { researchMode?: boolean };
        }
      ).metadata?.researchMode;

      return (
        <Box className="p-3">
          {hasResearchMode && (
            <Box className="flex-row items-center gap-1 pb-2">
              <Icon
                as={Search}
                size="sm"
                stroke={isDark ? "#60a5fa" : "#3b82f6"}
              />
              <Text
                variant="unstyled"
                className="font-medium text-caption"
                // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: theme-forked research-mode blue (#60a5fa/#3b82f6) with no matching semantic token
                UNSAFE_style={
                  colorStyle(isDark ? "#60a5fa" : "#3b82f6") as Record<
                    string,
                    string | number
                  >
                }
              >
                Research Mode
              </Text>
            </Box>
          )}
          <Text
            variant="unstyled"
            className="max-w-full shrink flex-wrap text-title"
            // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: data-driven theme userText color + lineHeight 22 (no leading token)
            UNSAFE_style={
              userMessageTextStyle(colors.messageBubble.userText) as Record<
                string,
                string | number
              >
            }
          >
            {messageProps.currentMessage?.text}
          </Text>
        </Box>
      );
    },
    [windowWidth, isDark, colors.messageBubble.userText]
  );

  const renderBubble = useCallback(
    (props: BubbleProps<IMessage>) => {
      try {
        const message = props.currentMessage;
        const previousMessage = props.previousMessage;
        const nextMessage = props.nextMessage;

        // Safety check for message structure
        if (!message || !message.user) {
          console.warn("Message or message.user is undefined:", message);
          return null;
        }

        // Handle progress messages
        if (message.user._id === "progress") {
          return (
            <Box>
              <QueryProgressBar messageId={message._id} />
            </Box>
          );
        }

        // Calculate iMessage-like spacing - with null safety
        const isSameUser = previousMessage?.user?._id === message?.user?._id;
        const isNextSameUser = nextMessage?.user?._id === message?.user?._id;

        // iMessage spacing logic:
        // - 2px between consecutive messages from same user
        // - 8px between messages from different users
        // - 4px margin top for first message in a group
        // - Reduced spacing between first user message and first AI response
        const isAIMessage = message?.user?._id === aiUser._id;
        const isPreviousUser = previousMessage?.user?._id !== aiUser._id;
        const isFirstAIResponse = isAIMessage && isPreviousUser && !isSameUser;

        const marginTop = isSameUser ? 1 : isFirstAIResponse ? 2 : 4;
        const marginBottom = isNextSameUser ? 1 : 8;

        const roleTestID = isAIMessage
          ? TEST_ID_GEMINI_CHAT.AI_MESSAGE
          : TEST_ID_GEMINI_CHAT.USER_MESSAGE;

        return (
          <Box
            testID={roleTestID}
            {...{
              "data-testid": roleTestID,
              "data-role": isAIMessage ? "ai" : "user",
            }}
          >
            <Bubble
              {...props}
              wrapperStyle={{
                left: {
                  backgroundColor: colors.messageBubble.background,
                  borderRadius: RADIUS.xl,
                  marginVertical: 0,
                  // SE-8272: the AI answer previously carried only a
                  // background fill — no border/shadow separated it from the
                  // page, so it read as an unstyled flat rectangle rather
                  // than a card. A 1px outline-default border (the same
                  // token the progress bar card already borders itself
                  // with) gives it a visible edge in both themes.
                  borderWidth: 1,
                  borderColor: colors.messageBubble.border.default,
                },
                right: {
                  backgroundColor: colors.messageBubble.userBackground,
                  borderRadius: RADIUS.xl,
                  marginVertical: 0,
                  // SE-7084: the user bubble is the only chat surface with no
                  // width cap. GiftedChat's right-hand wrapper is sized
                  // fit-content against the full row, so a long query stretched
                  // the solid blue box to nearly the window width. The AI
                  // bubble caps its content Box with the same ladder; this caps
                  // the coloured wrapper itself.
                  maxWidth: bubbleMaxWidth(windowWidth),
                },
              }}
              containerStyle={{
                left: {
                  borderRadius: RADIUS.xl,
                  marginTop,
                  marginBottom,
                  marginLeft: 8,
                  marginRight: "auto",
                },
                right: {
                  borderRadius: RADIUS.xl,
                  marginTop,
                  marginBottom,
                  marginLeft: "auto",
                  marginRight: 8,
                },
              }}
              renderMessageText={renderMessageText}
            />
          </Box>
        );
      } catch (error) {
        console.error("Error in renderBubble:", error);
        return null;
      }
    },
    [
      colors.messageBubble.background,
      colors.messageBubble.border.default,
      colors.messageBubble.userBackground,
      renderMessageText,
      windowWidth,
    ]
  );

  const renderSend = useCallback(
    (props: SendProps<IMessage>) => {
      // Check if the current text input is valid
      const currentText = props.text || " ";
      const shouldShowSend =
        canSendMessage && currentText.trim().length > 0 && !isLoading;

      return (
        <Send
          {...props}
          disabled={!shouldShowSend}
          containerStyle={{
            paddingTop: 0,
            paddingBottom: 0,
            display: shouldShowSend ? "flex" : "none",
          }}
        >
          <Box className="py-0">
            <Text
              variant="unstyled"
              className="text-title-sm"
              // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: data-driven theme userBackground text color
              UNSAFE_style={
                sendTextStyle(colors.messageBubble.userBackground) as Record<
                  string,
                  string | number
                >
              }
            >
              Send
            </Text>
          </Box>
        </Send>
      );
    },
    [isLoading, canSendMessage, colors.messageBubble.userBackground]
  );

  const renderInputToolbar = useCallback(
    (props: InputToolbarProps<IMessage>) => (
      <ChatInputToolbar
        props={props}
        windowWidth={windowWidth}
        isDark={isDark}
        isResearchToggleEnabled={isResearchToggleEnabled}
        isResearchMode={isResearchMode}
        onToggleResearch={onToggleResearch}
        canSend={canSendMessage && !isLoading}
      />
    ),
    [
      isDark,
      isResearchMode,
      isResearchToggleEnabled,
      windowWidth,
      onToggleResearch,
      canSendMessage,
      isLoading,
    ]
  );

  const renderFooter = useCallback(() => {
    if (isLoading || currentStatus) {
      return (
        <Box className="items-center pt-2">
          {currentStatus ? (
            <Box
              className="w-full"
              // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: measured responsive maxWidth from windowWidth (continuous multiplier); no closed sizing token
              UNSAFE_style={
                bubbleMaxWidthStyle(windowWidth) as Record<
                  string,
                  string | number
                >
              }
            >
              <QueryProgressBar messageId="test-progress" />
            </Box>
          ) : null}
          {isLoading ? (
            <Box className="pt-2">
              <LoadingDots />
            </Box>
          ) : null}
        </Box>
      );
    }
    return null;
  }, [isLoading, currentStatus, windowWidth]);

  const renderChatEmpty = useCallback(() => {
    return null; // No empty state content needed anymore
  }, []);

  const suggestedQuestions: SuggestedQuestion[] = useMemo(
    () => [
      {
        icon: "🎯",
        text: "Of players we've scouted recently, who fits our game model the best?",
        query:
          "Of players we've scouted recently, who fits our game model the best?",
      },
      {
        icon: "⭐",
        text: "Who are the top 5 left wingers under 20M and are good at carrying?",
        query:
          "Who are the top 5 left wingers under 20M and are good at carrying?",
      },
      {
        icon: "🔍",
        text: "What are the team style fit scores for the top 10 GPR ranked wingers in Ligue 1?",
        query:
          "What are the team style fit scores for the top 10 GPR ranked wingers in Ligue 1?",
      },
      {
        icon: "⚠️",
        text: "What players do our scouts disagree about and why?",
        query: "What players do our scouts disagree about and why?",
      },
    ],
    []
  );

  const renderSuggestedQuestions = useCallback(() => {
    return (
      <SuggestedColumn width={windowWidth} expanded={showSuggestedQuestions}>
        <Pressable
          className={`${showSuggestedQuestions ? "py-4" : "pb-2"} flex-row items-center justify-between`}
          onPress={onToggleSuggestedQuestions}
        >
          <Text
            variant="unstyled"
            className="font-medium text-title-lg"
            // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: isDark-forked pure #fff/#000 (does not match content-primary token hex)
            UNSAFE_style={
              colorStyle(isDark ? "#fff" : "#000") as Record<
                string,
                string | number
              >
            }
          >
            Suggested questions
          </Text>
          <Text
            variant="unstyled"
            className="text-title"
            // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: runtime rotate transform toggled on expanded state + theme-forked chevron color
            UNSAFE_style={
              chevronStyle(
                isDark ? "#888" : "#666",
                showSuggestedQuestions
              ) as Record<string, string | number>
            }
          >
            ▼
          </Text>
        </Pressable>

        {showSuggestedQuestions && (
          <Box
            // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: windowWidth-driven flexDirection/gap/flexWrap (continuous 768/1280 thresholds)
            UNSAFE_style={
              suggestedLayoutStyle(windowWidth, isMobile) as Record<
                string,
                string | number
              >
            }
          >
            {suggestedQuestions.map((question, index) => (
              <SuggestedQuestionItem
                key={index}
                question={question}
                onPress={onSuggestedQuestionPress}
              />
            ))}
          </Box>
        )}
      </SuggestedColumn>
    );
  }, [
    isDark,
    showSuggestedQuestions,
    windowWidth,
    isMobile,
    suggestedQuestions,
    onToggleSuggestedQuestions,
    onSuggestedQuestionPress,
  ]);

  // Mobile UI elements
  const mobileErrorDisplay = useMemo(
    () =>
      error ? (
        <Box
          // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: data-driven error-surface colors (theme-forked bg + #f44336 border) with no matching semantic token
          UNSAFE_style={
            errorBoxStyle(isDark) as Record<string, string | number>
          }
        >
          <Text
            variant="body"
            // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: theme-forked error text color with no matching semantic token
            UNSAFE_style={
              errorTextStyle(isDark) as Record<string, string | number>
            }
          >
            {error || "An error occurred"}
          </Text>
        </Box>
      ) : null,
    [error, isDark]
  );

  const mobileMessageKeyExtractor = useCallback(
    (item: IMessage) => item._id.toString(),
    []
  );

  const mobileRenderItem = useCallback(
    ({ item, index }: { item: IMessage; index: number }) => (
      <MessageBubble
        message={item}
        previousMessage={messages[index - 1]}
        nextMessage={messages[index + 1]}
      />
    ),
    [messages]
  );

  const isAwaitingAnswer = isAwaitingFirstToken(messages, isLoading);

  const mobileListFooter = useMemo(
    () =>
      currentStatus || isAwaitingAnswer ? (
        <Box className="px-1 py-2">
          <QueryProgressBar messageId="mobile-progress" />
        </Box>
      ) : null,
    [currentStatus, isAwaitingAnswer]
  );

  const mobileFlatList = useMemo(
    () => (
      <FlashList
        ref={mobileListRef}
        onContentSizeChange={onMobileListContentSize}
        onLayout={onMobileListLayout}
        onScrollBeginDrag={onMobileListUserScroll}
        onScroll={onMobileListScroll}
        data={messages}
        keyExtractor={mobileMessageKeyExtractor}
        renderItem={mobileRenderItem}
        maintainVisibleContentPosition={{
          autoscrollToTopThreshold: 0,
        }}
        keyboardDismissMode="interactive"
        keyboardShouldPersistTaps="handled"
        className="flex-1"
        // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: data-driven theme background (#000/#fff); FlashList atom is a bare re-export with no UNSAFE_style prop
        style={flashListStyle(isDark)}
        contentContainerStyle={mobileListContentStyle(
          bottomInset,
          iosKeyboardHeight
        )}
        ListFooterComponent={mobileListFooter}
      />
    ),
    [
      messages,
      isDark,
      bottomInset,
      iosKeyboardHeight,
      mobileMessageKeyExtractor,
      mobileRenderItem,
      mobileListFooter,
      onMobileListContentSize,
      onMobileListLayout,
      onMobileListUserScroll,
      onMobileListScroll,
    ]
  );

  const mobileChatInput = useMemo(
    () => (
      <ChatInput
        onSend={onSendText}
        onMessageSent={onToggleSuggestedQuestions}
        canSend={canSendMessage && !isLoading}
        isResearchMode={isResearchMode}
        onToggleResearch={onToggleResearch}
        isResearchToggleEnabled={isResearchToggleEnabled}
        placeholder="Ask about player performance..."
        showSuggestedQuestions={messages.length === 0 && showSuggestedQuestions}
        suggestedQuestions={
          messages.length === 0
            ? [
                {
                  icon: "🎯",
                  text: "Of players we've scouted recently, who fits our game model the best?",
                  query:
                    "Of players we've scouted recently, who fits our game model the best?",
                },
                {
                  icon: "⭐",
                  text: "Who are the top 5 left wingers under 20M and are good at carrying?",
                  query:
                    "Who are the top 5 left wingers under 20M and are good at carrying?",
                },
                {
                  icon: "🔍",
                  text: "What are the team style fit scores for the top 10 GPR ranked wingers in Ligue 1?",
                  query:
                    "What are the team style fit scores for the top 10 GPR ranked wingers in Ligue 1?",
                },
                {
                  icon: "⚠️",
                  text: "What players do our scouts disagree about and why?",
                  query: "What players do our scouts disagree about and why?",
                },
              ]
            : []
        }
        onToggleSuggestedQuestions={onToggleSuggestedQuestions}
        autoFocus={shouldAutofocusInput}
      />
    ),
    [
      canSendMessage,
      isLoading,
      isResearchMode,
      isResearchToggleEnabled,
      messages.length,
      showSuggestedQuestions,
      onSendText,
      onToggleSuggestedQuestions,
      onToggleResearch,
      shouldAutofocusInput,
    ]
  );

  // GiftedChat requires reversed message order (newest last)
  const reversedMessages = useMemo(() => [...messages].reverse(), [messages]);

  // SE-8674: once the user's own question is in the list, bring it into view
  // with its status rows and answer above the composer (see runQuestionPin).
  // Without this a follow-up appended under a tall answer landed below
  // FlashList's draw window and never rendered until the user scrolled. Only
  // a send arms it: a thread load never scrolls, and a send that added no
  // question is forgotten once the conversation settles or is cleared.
  useEffect(() => {
    const pending = pendingSendRef.current;
    if (!pending) return;
    const sentIndex = findSentQuestionIndex(messages, pending);
    if (sentIndex < 0) {
      if (messages.length < pending.fromIndex || !isLoading)
        pendingSendRef.current = null;
      return;
    }
    pendingSendRef.current = null;
    releaseQuestionPin(pinRefs);
    questionPinRef.current = {
      index: sentIndex,
      messageId: messages[sentIndex]._id,
      seen: false,
      ending: false,
      endPasses: 0,
      quietPasses: 0,
      seenVersion: -1,
    };
    attachUserScrollRelease(pinRefs);
  }, [messages, isLoading, pinRefs]);

  // Each update queues a pass; a replaced conversation releases the pin, and
  // the stream's end asks for one last reveal.
  useEffect(() => {
    const pin = questionPinRef.current;
    if (!pin) return;
    if (messages[pin.index]?._id !== pin.messageId) {
      releaseQuestionPin(pinRefs);
      return;
    }
    if (!isLoading && !pin.ending)
      questionPinRef.current = { ...pin, ending: true };
    scheduleQuestionPin(pinRefs);
  }, [messages, isLoading, pinRefs]);

  // Nothing of the pin outlives the screen.
  useEffect(() => () => releaseQuestionPin(pinRefs), [pinRefs]);

  // Android keyboard height tracking (edge-to-edge overlay — SE-5440)
  useEffect(() => {
    if (Platform.OS !== "android") return;

    const showSub = Keyboard.addListener("keyboardDidShow", e => {
      setAndroidKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener("keyboardDidHide", () => {
      setAndroidKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  // iOS native keyboard height tracking (avoids Reanimated 4.x animated style issues)
  useEffect(() => {
    if (Platform.OS !== "ios") return;

    const showSub = Keyboard.addListener("keyboardDidShow", e => {
      setIosKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener("keyboardDidHide", () => {
      setIosKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  // Reset chat state on mount to clear any stale data from previous session.
  // Cleanup on mount (not unmount) eliminates unmount overhead, making navigation
  // away instant. useLayoutEffect ensures state is reset before first paint. (SE-3755)
  useLayoutEffect(() => {
    clearMessages();
    clearAgentProgress();
    resetReconnectAttempts();
    resetChatConfigs();
    hideStatusDisplay();
    connectionStatusVar("disconnected");
  }, [
    clearMessages,
    clearAgentProgress,
    resetReconnectAttempts,
    resetChatConfigs,
    hideStatusDisplay,
  ]);

  // Hide suggested questions when keyboard appears
  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      "keyboardDidShow",
      () => {
        setShowSuggestedQuestions(false);
      }
    );

    return () => {
      keyboardDidShowListener.remove();
    };
  }, []);

  // Auto-send the ?q= prefill from the Home example-prompt chips. Guarded on the
  // query VALUE so a second chip is never swallowed by the first chip's send
  // (SE-6900).
  usePrefillAutoSend({
    prefillQuery,
    canSendMessage,
    sendMessage,
    createNewThread,
  });

  return (
    <ChatView
      isDark={isDark}
      messages={messages}
      isLoading={isLoading}
      error={error}
      connectionStatus={connectionStatus}
      reconnectAttempts={reconnectAttempts}
      showStatusSettings={showStatusSettings}
      isMobile={isMobile}
      headerHeight={headerHeight}
      bottomInset={bottomInset}
      androidKeyboardHeight={androidKeyboardHeight}
      tabBarHeight={tabBarHeight}
      keyboardHeight={keyboardHeight}
      inputAnimatedStyle={inputAnimatedStyle}
      staticKeyboardHeight={iosKeyboardHeight}
      colors={colors}
      onSend={onSend}
      onToggleStatusSettings={onToggleStatusSettings}
      onSuggestedQuestionPress={onSuggestedQuestionPress}
      renderBubble={renderBubble}
      renderSend={renderSend}
      renderInputToolbar={renderInputToolbar}
      renderFooter={renderFooter}
      renderChatEmpty={renderChatEmpty}
      renderSuggestedQuestions={renderSuggestedQuestions}
      reversedMessages={reversedMessages}
      mobileErrorDisplay={mobileErrorDisplay}
      mobileFlatList={mobileFlatList}
      mobileChatInput={mobileChatInput}
    />
  );
};

ChatContainer.displayName = "ChatContainer";

/* eslint-enable max-lines -- Re-enable max-lines rule after container */

export default ChatContainer;
